import { Button } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { H2 } from '@ui';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { reduxForm } from 'redux-form';

import {
    DELETE_DEBIT_CARD,
    SELECT_REPAYMENT_ROLE,
    UPDATE_DEBIT_CARD
} from './EditPaymentMethodModal.actions';
import messages from './EditPaymentMethodModal.messages';
import { FormField } from '../../../forms/formField';
import { CLOSE_MODAL } from '../../../iwModals/Modals.actions';
import promptMessages from '../../Profile/promptSave/PromptSave.messages';
import { CancelAction } from '../../ui/confirmationResponse/confirmationQuestion/cancelAction/CancelAction';
import { ConfirmAction } from '../../ui/confirmationResponse/confirmationQuestion/confirmAction/ConfirmAction';
import { ConfirmationQuestion } from '../../ui/confirmationResponse/confirmationQuestion/ConfirmationQuestion';
import { ConfirmationResponse } from '../../ui/confirmationResponse/ConfirmationResponse';
import { ConfirmationStatement } from '../../ui/confirmationResponse/confirmationStatement/ConfirmationStatement';

const formConfiguration = {
    form: 'editDebitCard'
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    closeModal: () => dispatch({ type: CLOSE_MODAL }),
    deleteDebitCard: (id: string) =>
        dispatch({ type: DELETE_DEBIT_CARD, paymentMethodID: id }),
    updateDebitCard: (debitCardData: {
        paymentMethodID: string;
        repaymentRole: string | null;
        fundingRole: string;
    }) => dispatch({ type: UPDATE_DEBIT_CARD, ...debitCardData }),
    selectRepaymentRole: (repaymentRole: string | null) =>
        dispatch({ type: SELECT_REPAYMENT_ROLE, repaymentRole })
});

const mapStateToProps = ({
    editPaymentMethodModal
}: {
    editPaymentMethodModal: RootState['editPaymentMethodModal'];
}) => ({
    ...editPaymentMethodModal,
    initialValues: {
        repaymentRole: editPaymentMethodModal.isPrimary
    }
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

function EditDebitCard(props: PropsFromRedux) {
    const {
        deleteDebitCard,
        selectRepaymentRole,
        paymentMethodID,
        repaymentRole,
        fundingRole,
        updateDebitCard,
        closeModal,
        updatingDebitCard,
        cardNumber
    } = props;

    const handleSubmit = () => {
        deleteDebitCard(paymentMethodID!);
    };

    const handleUpdateDebitCard = () => {
        updateDebitCard({
            paymentMethodID: paymentMethodID!,
            repaymentRole: repaymentRole,
            fundingRole: fundingRole!
        });
    };

    return (
        <div data-testid="Edit-Payment-Modal">
            <H2 className="mb-xl pt-s">
                <FormattedMessage
                    {...messages.editDebitCardTitle}
                    values={{
                        cardNumber: cardNumber?.slice(cardNumber.length - 4)
                    }}
                />
            </H2>

            <form className="mb-xl">
                <FormField
                    name="repaymentRole"
                    type="checkbox"
                    label={
                        'Use as default payment method for automatic repayments'
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        selectRepaymentRole(e.target.checked ? 'primary' : null)
                    }
                    className="mb-0"
                />
            </form>

            <ConfirmationResponse className="mb-xl" onSubmit={handleSubmit}>
                <ConfirmationStatement>
                    <FormattedMessage {...messages.deletePaymentMethod} />
                </ConfirmationStatement>

                <ConfirmationQuestion>
                    <FormattedMessage {...messages.deleteConfirmation} />

                    <ConfirmAction>
                        <FormattedMessage {...messages.confirmAction} />
                    </ConfirmAction>

                    <CancelAction>
                        <FormattedMessage {...messages.cancelAction} />
                    </CancelAction>
                </ConfirmationQuestion>
            </ConfirmationResponse>

            <div className="flex justify-between">
                <Button onClick={closeModal} variant="tertiary">
                    <FormattedMessage {...promptMessages.cancel} />
                </Button>

                <Button
                    onClick={handleUpdateDebitCard}
                    loading={updatingDebitCard}
                >
                    <FormattedMessage {...promptMessages.save} />
                </Button>
            </div>
        </div>
    );
}

export const EditDebitCardForm = connect(
    mapStateToProps,
    mapDispatchToProps
)(reduxForm<object, any>(formConfiguration)(EditDebitCard));
